import React, { Component } from 'react';
import propTypes from 'prop-types';
import { connect } from 'react-redux';
import { DropTarget } from 'react-dnd';
import flatten from 'lodash.flatten';

import { setCurrentEvent, openPresetModal, updateEvent } from '~/Redux/Modules/Events';
import { findCalendarInList, mapCalendarsToList } from '~/Redux/Modules/CalendarAccounts';
import { setDayType } from '~/Redux/Modules/DateRange';

import StyledEvent from '~/Containers/Pages/Calendar/StyledEvent/StyledEvent';
import { DATE_TIME_FORMAT, DATE_FORMAT, TIME_FORMAT, TABLET_WIDTH } from '~/Redux/Constants';
import { MONTH_EVENT } from '~/Config/DragTypes';
import moment from '~/Providers/Moment';

const COLLAPSED_EVENT_NUM = 2;

export const eventTarget = {
  canDrop: ({ start }, monitor) => {
    const previous = monitor.getItem().event;
    return (
      moment(previous.start, DATE_TIME_FORMAT).format(DATE_FORMAT) !== start.format(DATE_FORMAT)
    );
  },
  drop: ({ updateEvent, start, currentView, currentViewStart }, monitor) => {
    const event = monitor.getItem().event;
    var newStart = moment(
      `${start.format(DATE_FORMAT)} ${moment(event.start, DATE_TIME_FORMAT).format(TIME_FORMAT)}`,
      DATE_TIME_FORMAT
    );
    var newEnd = newStart
      .clone()
      .add(moment(event.end, DATE_TIME_FORMAT).diff(moment(event.start, DATE_TIME_FORMAT)));
    updateEvent(
      {
        ...event,
        start: newStart.format(DATE_TIME_FORMAT),
        end: newEnd.format(DATE_TIME_FORMAT),
        displayStart: newStart.format(DATE_TIME_FORMAT),
        displayEnd: newEnd.format(DATE_TIME_FORMAT),
        confirm: 'this_event',
      },
      currentView,
      currentViewStart
    );
  },
};

const dropCollect = (connect, monitor) => ({
  connectDropTarget: connect.dropTarget(),
  isOver: monitor.isOver(),
});

@DropTarget(MONTH_EVENT, eventTarget, dropCollect)
class AllDayEventDropWrapper extends Component {
  toggle = (e, change) => {
    e.stopPropagation();
    this.props.toggleExpand(change);
  };

  generateMoreToggle = () => {
    const { index, eventIndexes } = this.props;
    const eventsForDay = eventIndexes
      .map(eventRow => eventRow[index])
      .filter(event => event != null);
    if (
      !this.props.expanded &&
      eventIndexes &&
      eventIndexes.length > COLLAPSED_EVENT_NUM &&
      eventIndexes.filter(eventRow => eventRow[this.props.index]).length > COLLAPSED_EVENT_NUM
    ) {
      return (
        <div onClick={e => this.toggle(e, true)} className="more-events">
          <span className="tablet">+ {eventsForDay.length - COLLAPSED_EVENT_NUM} MORE</span>
          <span className="mobile">+ {eventsForDay.length - COLLAPSED_EVENT_NUM}</span>
        </div>
      );
    }
  };

  render() {
    const {
      eventIndexes,
      events,
      singleDay,
      connectDropTarget,
      index,
      expanded,
      toggleExpand,
      ...props
    } = this.props;
    const sliceItems = events => {
      if (expanded) return events;
      return events.slice(0, COLLAPSED_EVENT_NUM);
    };
    return connectDropTarget(
      <div {...props} className="all-day-events flex-1">
        {eventIndexes &&
          sliceItems(eventIndexes).map((eventRow, rowIndex) => {
            if (!eventRow[index]) return null;

            let event = events.find(
              event =>
                event.id === eventRow[index].id &&
                (!event.repeats ||
                  moment(event.start, DATE_TIME_FORMAT).isSame(this.props.start, 'day'))
            );
            event = event || events.find(event => event.id === eventRow[index].id);

            if (!event) return null;

            // Only render the summary if this is the first instance of the event for the week

            const isFirstSlot =
              typeof eventRow[index - 1] === 'undefined' ||
              eventRow[index - 1] === null ||
              eventRow[index - 1].id !== event.id ||
              eventRow[index - 1]?.startForRecurrence !== event.startForRecurrence;
            const isLastSlot =
              typeof eventRow[index + 1] === 'undefined' ||
              eventRow[index + 1] === null ||
              eventRow[index + 1].id !== event.id ||
              eventRow[index + 1]?.startForRecurrence !== event.startForRecurrence;

            const lastSlotStyle = isLastSlot ? { width: 'calc(100% - 0.5rem)' } : {};
            const style = {
              top: rowIndex > 0 ? 20 * rowIndex + rowIndex : 20 * rowIndex,
              ...lastSlotStyle,
              key: event.id,
            };
            return (
              <StyledEvent
                key={event.id}
                event={event}
                className="all-day-event"
                style={style}
                showSummary={isFirstSlot || singleDay}
              />
            );
          })}
        {this.generateMoreToggle()}
      </div>
    );
  }
}

class AllDayEvents extends Component {
  handleMakeAllDayEvent = start => {
    const { openPresetModal, dayView } = this.props;
    if (window.innerWidth < TABLET_WIDTH) {
      return dayView(start);
    }
    openPresetModal({
      forceUpdate: true,
      isAllDay: true,
      start: start
        .clone()
        .startOf('day')
        .format(DATE_TIME_FORMAT),
      end: start
        .clone()
        .startOf('day')
        .add(1, 'day')
        .format(DATE_TIME_FORMAT),
      color: this.props.defaultCalendar.color,
    });
  };

  render() {
    const {
      days,
      events,
      week,
      eventIndexes,
      singleDay,
      start,
      updateEvent,
      currentView,
      currentViewStart,
      expanded,
      toggleExpand,
    } = this.props;

    // if we are on the day view we only want the current day, all day events
    let daysArray = days;

    if (singleDay) {
      daysArray = Array(7).fill(null);
      daysArray[start.weekday()] = true;
    }

    return week.map((day, index) => {
      if (!daysArray[index]) return null;

      return (
        <AllDayEventDropWrapper
          key={index}
          {...{
            updateEvent,
            currentView,
            currentViewStart,
            index,
            expanded,
            toggleExpand,
          }}
          onClick={() => this.handleMakeAllDayEvent(day)}
          eventIndexes={eventIndexes}
          events={events}
          singleDay={singleDay}
          start={day}
        />
      );
    });
  }
}

AllDayEvents.propTypes = {
  days: propTypes.object,
  week: propTypes.array,
  start: propTypes.object,
  singleDay: propTypes.bool,
  eventIndexes: propTypes.array,
  events: propTypes.array,
};

AllDayEvents.defaultProps = {
  singleDay: false,
};

const mapStateToProps = ({ Events, DateRange, UserOptions, CalendarAccounts }) => {
  return {
    start: DateRange.start,
    end: DateRange.start,
    events: flatten(Object.values(Events.data)),
    defaultCalendar:
      findCalendarInList(mapCalendarsToList(CalendarAccounts), UserOptions.data.calendar_id) || {},
    currentView: DateRange.currentType,
    currentViewStart: DateRange.start.clone(),
  };
};

const mapDispatchToProps = dispatch => {
  return {
    setCurrentEvent: event => dispatch(setCurrentEvent(event)),
    openPresetModal: update => dispatch(openPresetModal(update)),
    updateEvent: (data, currentView, currentViewStart) =>
      dispatch(updateEvent(data, currentView, currentViewStart)),
    dayView: currentDay => dispatch(setDayType(currentDay.clone())),
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(AllDayEvents);
